// ✅ src/pages/Asset/AssetMaster.jsx
// FULL FILE - localStorage headers integrated

import React, { useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";
import HeaderBar from "../../components/HeaderBar";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";
import AssetLayoutWrapper from "../../components/AssetLayoutWrapper";
import { useNavigate, useLocation } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  FaArrowLeft,
  FaPlus,
  FaSave,
  FaTrash,
  FaUpload,
  FaSyncAlt,
} from "react-icons/fa";

const API_BASE = "https://nissi-india.com/api/assetMaster";

// ✅ localStorage helper
const getLS = (key, fallback = "") => {
  const val = localStorage.getItem(key);
  return val !== null && val !== undefined ? val : fallback;
};

// ✅ normalize "asset name" from SP recordset (robust)
const normalizeAssetTypeOptions = (rows = []) => {
  const opts = (rows || []).map((x, idx) => {
    const value =
      x?.fld_assettypeid ??
      x?.fld_assetid ??
      x?.fld_id ??
      x?.id ??
      x?.value ??
      x?.Value ??
      "";
    const label =
      x?.AssetType ??
      x?.fld_assetname ??
      x?.fld_assettype ??
      x?.name ??
      x?.text ??
      x?.Text ??
      "";
    return {
      key: `${value}-${idx}`,
      value: String(value ?? ""),
      label: String(label ?? ""),
      raw: x,
    };
  });

  return opts.filter((o) => o.value && o.value !== "0");
};

// ✅ normalize subtype options
const normalizeSubTypeOptions = (rows = []) => {
  const opts = (rows || []).map((x, idx) => {
    const value = x?.fld_assetsubtypeid ?? x?.id ?? x?.value ?? "";
    const label = x?.fld_assetsubtypename ?? x?.name ?? x?.text ?? "";
    return {
      key: `${value}-${idx}`,
      value: String(value ?? ""),
      label: String(label ?? ""),
      raw: x,
    };
  });
  return opts.filter((o) => o.value && o.value !== "0");
};

const AssetMaster = () => {
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;
  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;

  const navigate = useNavigate();
  const routerLocation = useLocation();

  // ✅ localStorage values
  const finYearId = getLS("finYearId");
  const finYear = getLS("finYear");
  const branchId = getLS("branchId") || getLS("branchid");
  const branchType = getLS("branchType") || getLS("branchtype");
  const userId = getLS("userId") || getLS("loginuserid");

  // ✅ common headers
  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchId,
      "x-branchtype": branchType,
      "x-userid": userId,
    }),
    [finYearId, finYear, branchId, branchType, userId]
  );

  const editId = useMemo(() => {
    const q = new URLSearchParams(routerLocation.search);
    const id = Number(q.get("id") || 0);
    return Number.isFinite(id) ? id : 0;
  }, [routerLocation.search]);

  const [panelType, setPanelType] = useState("L");

  const [dd, setDd] = useState({
    assetTypesVO: [],
    models: [],
    makes: [],
    vehicles: [],
    purchaseFrom: [],
    locations: [],
    assetTypesS: [],
    makesS: [],
    locationsS: [],
    assetClassesL: [],
    taxTypes: [],
  });

  const [subtypesVO, setSubtypesVO] = useState([]);
  const [subtypesS, setSubtypesS] = useState([]);
  const [subtypesL, setSubtypesL] = useState([]);

  const [vo, setVo] = useState({
    assetTypeId: "Select",
    assetSubTypeId: "Select",
    vehicleId: "Select",
    vehicleRegNo: "",
    description: "",
    idCodePrefix: "",
    idCodeStart: "",
    makeId: "Select",
    makeNewName: "",
    modelId: "Select",
    modelNewName: "",
    invoiceNo: "",
    machineSrNo: "",
    purchaseDate: "",
    purchasedFromId: "Select",
    purchasedFromOtherName: "",
    wdvDate: "",
    wdvPercentage: "",
    depreciationValue: "",
    wdvAsOn: "",
    noOfDays: "",
    chasisNo: "",
    engineNo: "",
    idle: "",
    hypothecated: "",
    calibratedDate: "",
    nextCalibrationDate: "",
    locationText: "Select",
    purchaseValue: "",
    noOfEntries: "1",
    invoiceDocs: [],
  });

  const [sm, setSm] = useState({
    assetTypeId: "Select",
    assetSubTypeId: "Select",
    makeId: "Select",
    idCodeNo: "",
    size: "",
    invoiceNo: "",
    purchaseDate: "",
    locationText: "Select",
    purchaseValue: "",
    noOfQty: "",
    totalValue: "",
    invoiceDocs: [],
  });

  const [land, setLand] = useState({
    assetClassId: "Select",
    assetSubTypeId: "Select",
    assetCode: "",
    plotNumber: "",
    location: "Select",
    address: "",
    propertyInNameOf: "",
    pattaNo: "",
    surveyNo: "",
    panchayathNo: "",
    sellerName: "",
    extend: "",
    acquisitionDate: "",
    purchaseValue: "",
    originalDocument: "",
    narration: "",
    saleDate: "",
    saleExtent: "",
    saleExtentRate: "",
    saleAmount: "",
    saleReceiver: "",
    saleRemark: "",
    taxDetails: [],
    documents: [],
    taxEntry: {
      taxId: "Select",
      yearFrom: "",
      yearTo: "",
      paydet: "",
      paidDate: "",
      renewalDate: "",
      amount: "",
      narration: "",
    },
    docTypeRadio: "land",
  });

  const inputClass = `w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
    isDark
      ? "bg-gray-700/30 border-gray-600 text-white placeholder-gray-400"
      : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
  }`;

  const labelClass = `block text-sm font-medium mb-2 ${
    isDark ? "text-gray-300" : "text-gray-700"
  }`;

  const cardClass = `rounded-2xl shadow-xl border ${
    isDark
      ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
      : "bg-white text-black border-gray-200"
  }`;

  const sectionClass = `rounded-xl border p-6 ${
    isDark ? "border-gray-700 bg-gray-800/50" : "border-gray-200 bg-blue-50"
  }`;

  const btnPrimary = `flex items-center gap-2 px-6 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${buttonClass} ${hoverClass} text-white`;

  const btnGray = `flex items-center gap-2 px-5 py-2 rounded-lg font-semibold shadow hover:shadow-md transition-all ${
    isDark
      ? "bg-gray-700 hover:bg-gray-600 text-white"
      : "bg-gray-200 hover:bg-gray-300 text-gray-900"
  }`;

  const loadInit = async (type) => {
    const t = type === "L" ? "L" : type;
    const res = await axios.get(`${API_BASE}/init`, {
      params: { type: t },
      headers: commonHeaders,
    });

    if (!res.data?.success) throw new Error(res.data?.message || "Init failed");

    if (t === "V" || t === "O") {
      setDd((p) => ({
        ...p,
        assetTypesVO: normalizeAssetTypeOptions(res.data.assetTypes || []),
        models: res.data.models || [],
        makes: res.data.makes || [],
        vehicles: res.data.vehicles || [],
        purchaseFrom: res.data.purchaseFrom || [],
        locations: res.data.locations || [],
      }));
    } else if (t === "S") {
      setDd((p) => ({
        ...p,
        assetTypesS: normalizeAssetTypeOptions(res.data.assetTypes || []),
        makesS: res.data.makes || [],
        locationsS: res.data.locations || [],
      }));
    } else {
      setDd((p) => ({
        ...p,
        assetClassesL: normalizeAssetTypeOptions(res.data.assetClasses || []),
        taxTypes: res.data.taxTypes || [],
        locations: res.data.locations || [],
      }));
    }
  };

  const loadSubtypes = async (assetTypeId) => {
    if (!assetTypeId || assetTypeId === "Select") return [];
    const res = await axios.get(`${API_BASE}/subtypes`, {
      params: { assetTypeId },
      headers: commonHeaders,
    });
    if (!res.data?.success)
      throw new Error(res.data?.message || "Subtypes failed");
    return normalizeSubTypeOptions(res.data.data || []);
  };

  useEffect(() => {
    (async () => {
      try {
        await loadInit("L");
      } catch (e) {
        toast.error(e.message || "Failed to load");
      }
    })();
  }, []);

  useEffect(() => {
    (async () => {
      try {
        await loadInit(panelType);

        if (panelType === "V" || panelType === "O") {
          setVo((p) => ({ ...p, invoiceDocs: [] }));
        }
        if (panelType === "S") {
          setSm((p) => ({ ...p, invoiceDocs: [] }));
        }
      } catch (e) {
        toast.error(e.message || "Init load failed");
      }
    })();
  }, [panelType, commonHeaders]);

  useEffect(() => {
    (async () => {
      if (!editId) return;
      try {
        const res = await axios.get(`${API_BASE}/asset/${editId}`, {
          headers: commonHeaders,
        });
        if (!res.data?.success)
          throw new Error(res.data?.message || "Failed to load edit");

        const a = res.data.data;
        if (!a) return;

        const at = String(a.fld_assettype || "");
        if (at === "V") setPanelType("V");
        else if (at === "S") setPanelType("S");
        else if (at === "L") setPanelType("L");
        else setPanelType("O");

        if (at === "S") {
          setSm((p) => ({
            ...p,
            assetTypeId: a.fld_assettypeid ? String(a.fld_assettypeid) : "Select",
            assetSubTypeId: a.fld_AssetSubTypeid
              ? String(a.fld_AssetSubTypeid)
              : "Select",
            makeId: a.fld_makeid ? String(a.fld_makeid) : "Select",
            idCodeNo: a.fld_idcodeno || "",
            size: a.fld_size || "",
            invoiceNo: a.fld_invoiceno || "",
            purchaseDate: a.fld_purchasedate
              ? String(a.fld_purchasedate).substring(0, 10)
              : "",
            locationText: a.fld_location || "Select",
            purchaseValue: a.fld_purchasevalue || "",
            noOfQty: a.fld_noofqty || "",
            totalValue: a.fld_totalvalue || "",
          }));
        } else if (at === "L") {
          // ✅ FIX: this branch never existed before — Land edits fell
          // through to the `else` (setVo) block, so `land` state was
          // never populated and every Land field stayed blank on edit.
          setLand((p) => ({
            ...p,
            assetClassId: a.fld_assettypeid ? String(a.fld_assettypeid) : "Select",
            assetSubTypeId: a.fld_assetsubtypeid
              ? String(a.fld_assetsubtypeid)
              : "Select",
            assetCode: a.fld_assetcode || "",
            plotNumber: a.fld_plotnumber || "",
            location: a.fld_location || "Select",
            address: a.fld_address || "",
            propertyInNameOf: a.fld_propertyname || "",
            pattaNo: a.fld_pattano || "",
            surveyNo: a.fld_surveyno || "",
            panchayathNo: a.fld_panchayathno || "",
            sellerName: a.fld_sellername || "",
            extend: a.fld_extend || "",
            acquisitionDate: a.fld_acquisitiondate
              ? String(a.fld_acquisitiondate).substring(0, 10)
              : "",
            purchaseValue: a.fld_PurchaseValue || "",
            originalDocument: a.fld_originaldoc || "",
            saleDate: a.fld_salesdate
              ? String(a.fld_salesdate).substring(0, 10)
              : "",
            saleExtent: a.fld_salesextent || "",
            saleExtentRate: a.fld_salesextentrate || "",
            saleAmount: a.fld_salesamount || "",
            saleReceiver: a.fld_salesreceiver || "",
            saleRemark: a.fld_salesremark || "",
          }));
        } else {
          setVo((p) => ({
            ...p,
            assetTypeId: a.fld_assettypeid ? String(a.fld_assettypeid) : "Select",
            assetSubTypeId: a.fld_AssetSubTypeid
              ? String(a.fld_AssetSubTypeid)
              : "Select",
            vehicleId: a.fld_vehicleid ? String(a.fld_vehicleid) : "Select",
            description: a.fld_description || "",
            idCodePrefix: a.fld_idcodeno || "",
            makeId: a.fld_makeid ? String(a.fld_makeid) : "Select",
            modelId: a.fld_modelid ? String(a.fld_modelid) : "Select",
            invoiceNo: a.fld_invoiceno || "",
            machineSrNo: a.fld_machinesrno || "",
            locationText: a.fld_location || "Select",
            purchaseValue: a.fld_purchasevalue || "",
            wdvPercentage: a.fld_Percentage || "",
            depreciationValue: a.fld_depreciationvalue || "",
            wdvAsOn: a.fld_wdv || "",
            noOfDays: a.fld_nodays || "",
            chasisNo: a.fld_chasisno || "",
            engineNo: a.fld_engineno || "",
            idle: a.fld_idle || "",
            hypothecated: a.fld_hypothicated || "",
          }));
        }
      } catch (e) {
        toast.error(e.message || "Edit load failed");
      }
    })();
  }, [editId, commonHeaders]);

  useEffect(() => {
    (async () => {
      try {
        if (panelType === "V" || panelType === "O") {
          const list = await loadSubtypes(vo.assetTypeId);
          setSubtypesVO(list);
        }
      } catch (e) {
        toast.error(e.message || "Failed to load subtypes");
      }
    })();
  }, [vo.assetTypeId, panelType]);

  useEffect(() => {
    (async () => {
      try {
        if (panelType === "S") {
          const list = await loadSubtypes(sm.assetTypeId);
          setSubtypesS(list);
        }
      } catch (e) {
        toast.error(e.message || "Failed to load subtypes");
      }
    })();
  }, [sm.assetTypeId, panelType]);

  useEffect(() => {
    (async () => {
      try {
        if (panelType === "L") {
          const list = await loadSubtypes(land.assetClassId);
          setSubtypesL(list);
        }
      } catch (e) {
        toast.error(e.message || "Failed to load land subtypes");
      }
    })();
  }, [land.assetClassId, panelType]);

  const uploadFile = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    const res = await axios.post(`${API_BASE}/upload`, fd, {
      headers: {
        ...commonHeaders,
        "Content-Type": "multipart/form-data",
      },
    });
    if (!res.data?.success)
      throw new Error(res.data?.message || "Upload failed");
    return { docName: res.data.fileName, docPath: res.data.filePath };
  };

  const handleVoAttachDoc = async (file) => {
    if (!file) return;
    try {
      const { docName, docPath } = await uploadFile(file);
      const dtype = panelType === "V" ? "V" : "O";
      setVo((p) => ({
        ...p,
        invoiceDocs: [...p.invoiceDocs, { type: dtype, docName, docPath }],
      }));
      toast.success("Document attached");
    } catch (e) {
      toast.error(e.message || "Attach failed");
    }
  };

  const handleSmAttachDoc = async (file) => {
    if (!file) return;
    try {
      const { docName, docPath } = await uploadFile(file);
      setSm((p) => ({
        ...p,
        invoiceDocs: [...p.invoiceDocs, { type: "S", docName, docPath }],
      }));
      toast.success("Document attached");
    } catch (e) {
      toast.error(e.message || "Attach failed");
    }
  };

  const handleLandAttachDoc = async (file) => {
    if (!file) return;
    try {
      const { docName, docPath } = await uploadFile(file);
      setLand((p) => ({
        ...p,
        documents: [
          ...p.documents,
          { docType: p.docTypeRadio, docName, docPath },
        ],
      }));
      toast.success("Document attached");
    } catch (e) {
      toast.error(e.message || "Attach failed");
    }
  };

  const addTaxRow = () => {
    const t = land.taxEntry;
    if (!t.taxId || t.taxId === "Select") return toast.error("Select Tax Type");
    if (!t.yearFrom || !t.yearTo) return toast.error("Enter Year From/To");
    if (!t.paidDate) return toast.error("Enter Paid Date");
    if (!t.renewalDate) return toast.error("Enter Renewal Date");
    if (!t.amount) return toast.error("Enter Amount");

    const taxName =
      dd.taxTypes.find((x) => String(x.fld_taxid) === String(t.taxId))
        ?.fld_taxtype || "";

    setLand((p) => ({
      ...p,
      taxDetails: [
        ...p.taxDetails,
        {
          taxId: String(t.taxId),
          taxName,
          yearFrom: t.yearFrom,
          yearTo: t.yearTo,
          paydet: t.paydet,
          paidDate: t.paidDate,
          renewalDate: t.renewalDate,
          amount: t.amount,
          narration: t.narration,
        },
      ],
      taxEntry: {
        taxId: "Select",
        yearFrom: "",
        yearTo: "",
        paydet: "",
        paidDate: "",
        renewalDate: "",
        amount: "",
        narration: "",
      },
    }));
    toast.success("Tax row added");
  };

  const removeTaxRow = (idx) => {
    setLand((p) => ({
      ...p,
      taxDetails: p.taxDetails.filter((_, i) => i !== idx),
    }));
  };

  const saveVehicleOthers = async () => {
    try {
      if (!vo.assetTypeId || vo.assetTypeId === "Select")
        return toast.error("Select Asset Name");
      if (panelType === "V" && !vo.vehicleRegNo?.trim())
        return toast.error("Vehicle Reg.No is required");
      if (!vo.idCodePrefix?.trim())
        return toast.error("ID Code.No is required");
      if (vo.locationText === "Select") return toast.error("Select Location");

      const mode = editId ? "U" : "N";

      const payload = {
        mode,
        assetId: editId || 0,
        loginUserId: userId,
        noOfEntries: vo.noOfEntries || "1",
        idCodePrefix: vo.idCodePrefix || "",
        idCodeStart: vo.idCodeStart || "",
        assetTypeId: vo.assetTypeId,
        assetSubTypeId:
          vo.assetSubTypeId !== "Select" ? vo.assetSubTypeId : null,
        vehicleId: vo.vehicleId !== "Select" ? vo.vehicleId : null,
        makeId: vo.makeId !== "Select" ? vo.makeId : null,
        makeNewName: vo.makeNewName || "",
        modelId: vo.modelId !== "Select" ? vo.modelId : null,
        modelNewName: vo.modelNewName || "",
        purchasedFromId:
          vo.purchasedFromId !== "Select" ? vo.purchasedFromId : null,
        purchasedFromOtherName: vo.purchasedFromOtherName || "",
        invoiceNo: vo.invoiceNo || "",
        machineSrNo: vo.machineSrNo || "",
        purchaseDate: vo.purchaseDate || "",
        calibratedDate: vo.calibratedDate || "",
        nextCalibrationDate: vo.nextCalibrationDate || "",
        wdvDate: vo.wdvDate || "",
        noOfDays: vo.noOfDays || "",
        wdvPercentage: vo.wdvPercentage || "",
        depreciationValue: vo.depreciationValue || "",
        wdvAsOn: vo.wdvAsOn || "",
        purchaseValue: vo.purchaseValue || "",
        chasisNo: vo.chasisNo || "",
        engineNo: vo.engineNo || "",
        idle: vo.idle || "",
        hypothecated: vo.hypothecated || "",
        description: vo.description || "",
        locationText: vo.locationText,
        invoiceDocs: vo.invoiceDocs || [],
      };

      const res = await axios.post(`${API_BASE}/vehicleOthers/save`, payload, {
        headers: commonHeaders,
      });
      if (!res.data?.success)
        throw new Error(res.data?.message || "Save failed");

      toast.success(mode === "U" ? "Updated Successfully" : "Saved Successfully");
      navigate("/Asset/assetMasterListing");
    } catch (e) {
      toast.error(e.message || "Save failed");
    }
  };

  const saveShuttering = async () => {
    try {
      if (!sm.assetTypeId || sm.assetTypeId === "Select")
        return toast.error("Select Asset Name");
      if (!sm.idCodeNo?.trim())
        return toast.error("ID Code.No is required");
      if (sm.locationText === "Select") return toast.error("Select Location");

      const mode = editId ? "U" : "N";

      const payload = {
        mode,
        assetId: editId || 0,
        loginUserId: userId,
        assetTypeId: sm.assetTypeId,
        assetSubTypeId:
          sm.assetSubTypeId !== "Select" ? sm.assetSubTypeId : null,
        makeId: sm.makeId !== "Select" ? sm.makeId : null,
        idCodeNo: sm.idCodeNo || "",
        size: sm.size || "",
        invoiceNo: sm.invoiceNo || "",
        purchaseDate: sm.purchaseDate || "",
        locationText: sm.locationText,
        purchaseValue: sm.purchaseValue || "",
        noOfQty: sm.noOfQty || "",
        totalValue: sm.totalValue || "",
        invoiceDocs: sm.invoiceDocs || [],
      };

      const res = await axios.post(`${API_BASE}/shuttering/save`, payload, {
        headers: commonHeaders,
      });
      if (!res.data?.success)
        throw new Error(res.data?.message || "Save failed");

      toast.success(mode === "U" ? "Updated Successfully" : "Saved Successfully");
      navigate("/Asset/assetMasterListing");
    } catch (e) {
      toast.error(e.message || "Save failed");
    }
  };

  const saveLand = async () => {
    try {
      if (!land.assetClassId || land.assetClassId === "Select")
        return toast.error("Select Asset Class");
      if (!land.assetCode) return toast.error("Asset Code is required");
      if (!land.plotNumber) return toast.error("Plot Number is required");
      if (!land.location || land.location === "Select")
        return toast.error("Location is required");
      if (!land.address) return toast.error("Address is required");
      if (!land.propertyInNameOf)
        return toast.error("Property in Name of is required");
      if (!land.pattaNo) return toast.error("Patta No is required");
      if (!land.surveyNo) return toast.error("Survey No is required");
      if (!land.sellerName) return toast.error("Seller Name is required");
      if (!land.extend) return toast.error("Extend is required");
      if (!land.acquisitionDate)
        return toast.error("Date of Purchase is required");

      if (land.taxDetails.length === 0) {
        if (!land.taxEntry.taxId || land.taxEntry.taxId === "Select")
          return toast.error("Select Tax Type");
        if (!land.taxEntry.yearFrom) return toast.error("Year From is required");
        if (!land.taxEntry.yearTo) return toast.error("Year To is required");
        if (!land.taxEntry.paydet)
          return toast.error("Payment details is required");
        if (!land.taxEntry.paidDate) return toast.error("Paid Date is required");
        if (!land.taxEntry.renewalDate)
          return toast.error("Next Renewal Date is required");
        if (!land.taxEntry.amount) return toast.error("Amount is required");
      }

      const mode = editId ? "U" : "N";

      const payload = {
        mode,
        assetId: editId || 0,
        loginUserId: userId,
        assetClassId: land.assetClassId,
        assetSubTypeId:
          land.assetSubTypeId !== "Select" ? land.assetSubTypeId : null,
        assetCode: land.assetCode || "",
        plotNumber: land.plotNumber || "",
        location: land.location || "",
        address: land.address || "",
        propertyInNameOf: land.propertyInNameOf || "",
        pattaNo: land.pattaNo || "",
        surveyNo: land.surveyNo || "",
        panchayathNo: land.panchayathNo || "",
        sellerName: land.sellerName || "",
        extend: land.extend || "",
        acquisitionDate: land.acquisitionDate || "",
        purchaseValue: land.purchaseValue || "",
        originalDocument: land.originalDocument || "",
        narration: land.narration || "",
        saleDate: land.saleDate || "",
        saleExtent: land.saleExtent || "",
        saleExtentRate: land.saleExtentRate || "",
        saleAmount: land.saleAmount || "",
        saleReceiver: land.saleReceiver || "",
        saleRemark: land.saleRemark || "",
        taxDetails: land.taxDetails || [],
        documents: land.documents || [],
      };

      const res = await axios.post(`${API_BASE}/land/save`, payload, {
        headers: commonHeaders,
      });
      if (!res.data?.success)
        throw new Error(res.data?.message || "Save failed");

      toast.success(mode === "U" ? "Updated Successfully" : "Saved Successfully");
      navigate("/Asset/assetMasterListing");
    } catch (e) {
      toast.error(e.message || "Save failed");
    }
  };

  useEffect(() => {
    const qty = Number(sm.noOfQty || 0);
    const val = Number(sm.purchaseValue || 0);
    if (!Number.isFinite(qty) || !Number.isFinite(val)) return;
    const total = qty * val;
    setSm((p) => ({ ...p, totalValue: total ? String(total) : "" }));
  }, [sm.noOfQty, sm.purchaseValue]);

  const topRadio = (
    <div className="mb-6">
      <div className="flex flex-wrap items-center gap-6">
        <span className={labelClass}>Asset Type</span>

        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="radio"
            name="panelType"
            checked={panelType === "L"}
            onChange={() => setPanelType("L")}
            className="w-4 h-4"
          />
          <span className={isDark ? "text-gray-300" : "text-gray-700"}>
            ImMovable (Land)
          </span>
        </label>

        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="radio"
            name="panelType"
            checked={panelType === "V"}
            onChange={() => setPanelType("V")}
            className="w-4 h-4"
          />
          <span className={isDark ? "text-gray-300" : "text-gray-700"}>
            Vehicle
          </span>
        </label>

        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="radio"
            name="panelType"
            checked={panelType === "O"}
            onChange={() => setPanelType("O")}
            className="w-4 h-4"
          />
          <span className={isDark ? "text-gray-300" : "text-gray-700"}>
            Movable (Others)
          </span>
        </label>

                <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="radio"
            name="panelType"
            checked={panelType === "S"}
            onChange={() => setPanelType("S")}
            className="w-4 h-4"
          />
          <span className={isDark ? "text-gray-300" : "text-gray-700"}>
            Scrap
          </span>
        </label>
      </div>
    </div>
  );

  const renderVehicleOthers = () => {
    const isVehicle = panelType === "V";

    return (
      <>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div className={sectionClass}>
            <h3
              className={`text-lg font-semibold mb-4 ${buttonClass} text-white p-3 rounded-lg`}
            >
              {isVehicle ? "Vehicle / Others Details" : "Others Details"}
            </h3>

            <div className="space-y-4">
              <div>
                <label className={labelClass}>Asset Name *</label>
                <select
                  className={inputClass}
                  value={vo.assetTypeId}
                  onChange={(e) =>
                    setVo((p) => ({
                      ...p,
                      assetTypeId: e.target.value,
                      assetSubTypeId: "Select",
                    }))
                  }
                >
                  <option value="Select">Select</option>
                  {dd.assetTypesVO.map((o) => (
                    <option key={o.key} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className={labelClass}>Asset Sub Type Name</label>
                <select
                  className={inputClass}
                  value={vo.assetSubTypeId}
                  onChange={(e) =>
                    setVo((p) => ({ ...p, assetSubTypeId: e.target.value }))
                  }
                >
                  <option value="Select">Select</option>
                  {subtypesVO.map((o) => (
                    <option key={o.key} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className={labelClass}>
                  {isVehicle ? "Vehicle Reg.No" : "Description"}{" "}
                  {isVehicle ? "*" : ""}
                </label>

                {isVehicle ? (
                  <>
                    <input
                      className={inputClass}
                      list="vehicleRegList"
                      value={vo.vehicleRegNo || ""}
                      onChange={(e) => {
                        const reg = e.target.value;

                        const match = (dd.vehicles || []).find((x) => {
                          const r = x.Vehicle || x.vehicleRegNo || x.regno || "";
                          return String(r).trim() === String(reg).trim();
                        });

                        setVo((p) => ({
                          ...p,
                          vehicleRegNo: reg,
                          vehicleId: match
                            ? String(
                                match.fld_vehicleid || match.vehicleId || match.id || ""
                              )
                            : "Select",
                        }));
                      }}
                      placeholder="Enter Vehicle Reg.No"
                    />

                    <datalist id="vehicleRegList">
                      {(dd.vehicles || []).map((x, idx) => {
                        const reg = x.Vehicle || x.vehicleRegNo || x.regno || "";
                        return <option key={idx} value={reg} />;
                      })}
                    </datalist>
                  </>
                ) : (
                  <textarea
                    className={inputClass}
                    rows={3}
                    value={vo.description}
                    onChange={(e) =>
                      setVo((p) => ({ ...p, description: e.target.value }))
                    }
                    placeholder="Enter description"
                  />
                )}
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={labelClass}>ID Code.No *</label>
                  <input
                    className={inputClass}
                    value={vo.idCodePrefix}
                    onChange={(e) =>
                      setVo((p) => ({ ...p, idCodePrefix: e.target.value }))
                    }
                    placeholder="Prefix"
                  />
                </div>
                <div>
                  <label className={labelClass}>Start No (for No.Of Entries)</label>
                  <input
                    className={inputClass}
                    value={vo.idCodeStart}
                    onChange={(e) =>
                      setVo((p) => ({ ...p, idCodeStart: e.target.value }))
                    }
                    placeholder="100"
                  />
                </div>
              </div>

              <div>
                <label className={labelClass}>Make</label>
                <div className="grid grid-cols-2 gap-3">
                  <select
                    className={inputClass}
                    value={vo.makeId}
                    onChange={(e) =>
                      setVo((p) => ({ ...p, makeId: e.target.value }))
                    }
                  >
                    <option value="Select">Select</option>
                    <option value="New">New</option>
                    {(dd.makes || []).map((x, idx) => (
                      <option
                        key={idx}
                        value={String(x.fld_Makeid || x.id || x.value || "")}
                      >
                        {x.Make || x.name || x.text || "Make"}
                      </option>
                    ))}
                  </select>
                  <input
                    className={inputClass}
                    value={vo.makeNewName}
                    onChange={(e) =>
                      setVo((p) => ({ ...p, makeNewName: e.target.value }))
                    }
                    placeholder="New make name (if New)"
                  />
                </div>
              </div>

              <div>
                <label className={labelClass}>Model/Capacity/HP</label>
                <div className="grid grid-cols-2 gap-3">
                  <select
                    className={inputClass}
                    value={vo.modelId}
                    onChange={(e) =>
                      setVo((p) => ({ ...p, modelId: e.target.value }))
                    }
                  >
                    <option value="Select">Select</option>
                    <option value="New">New</option>
                    {(dd.models || []).map((x, idx) => (
                      <option
                        key={idx}
                        value={String(x.fld_Modelid || x.id || x.value || "")}
                      >
                        {x.Model || x.name || x.text || "Model"}
                      </option>
                    ))}
                  </select>
                  <input
                    className={inputClass}
                    value={vo.modelNewName}
                    onChange={(e) =>
                      setVo((p) => ({ ...p, modelNewName: e.target.value }))
                    }
                    placeholder="New model name (if New)"
                  />
                </div>
              </div>

              <div>
                <label className={labelClass}>Serial No / Machine Sr.No</label>
                <input
                  className={inputClass}
                  value={vo.machineSrNo}
                  onChange={(e) =>
                    setVo((p) => ({ ...p, machineSrNo: e.target.value }))
                  }
                />
              </div>

              <div>
                <label className={labelClass}>Engine No</label>
                <input
                  className={inputClass}
                  value={vo.engineNo}
                  onChange={(e) =>
                    setVo((p) => ({ ...p, engineNo: e.target.value }))
                  }
                />
              </div>

              <div>
                <label className={labelClass}>Chasis No</label>
                <input
                  className={inputClass}
                  value={vo.chasisNo}
                  onChange={(e) =>
                    setVo((p) => ({ ...p, chasisNo: e.target.value }))
                  }
                />
              </div>

              <div>
                <label className={labelClass}>Location *</label>
                <select
                  className={inputClass}
                  value={vo.locationText}
                  onChange={(e) =>
                    setVo((p) => ({ ...p, locationText: e.target.value }))
                  }
                >
                  <option value="Select">Select</option>
                  {(dd.locations || []).map((x, idx) => (
                    <option
                      key={idx}
                      value={
                        x.fld_BranchName || x.fld_branch || x.name || x.text || ""
                      }
                    >
                      {x.fld_BranchName || x.fld_branch || x.name || x.text || "Branch"}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className={labelClass}>Idle</label>
                <input
                  className={inputClass}
                  value={vo.idle}
                  onChange={(e) => setVo((p) => ({ ...p, idle: e.target.value }))}
                />
              </div>

              <div>
                <label className={labelClass}>Calibrated Date</label>
                <input
                  type="date"
                  className={inputClass}
                  value={vo.calibratedDate}
                  onChange={(e) =>
                    setVo((p) => ({ ...p, calibratedDate: e.target.value }))
                  }
                />
              </div>

              <div>
                <label className={labelClass}>No.Of Entries</label>
                <input
                  className={inputClass}
                  value={vo.noOfEntries}
                  onChange={(e) =>
                    setVo((p) => ({ ...p, noOfEntries: e.target.value }))
                  }
                  placeholder="1"
                />
              </div>
            </div>
          </div>

          <div className={sectionClass}>
            <h3
              className={`text-lg font-semibold mb-4 ${buttonClass} text-white p-3 rounded-lg`}
            >
              Purchase / WDV
            </h3>

            <div className="space-y-4">
              <div>
                <label className={labelClass}>Purchased From</label>
                <select
                  className={inputClass}
                  value={vo.purchasedFromId}
                  onChange={(e) =>
                    setVo((p) => ({ ...p, purchasedFromId: e.target.value }))
                  }
                >
                  <option value="Select">Select</option>
                  {(dd.purchaseFrom || []).map((x, idx) => (
                    <option
                      key={idx}
                      value={String(x.fld_purchasefromid || x.id || x.value || "")}
                    >
                      {x.fld_purchasefrom || x.name || x.text || "From"}
                    </option>
                  ))}
                  <option value="Others">Others</option>
                </select>
              </div>

              {vo.purchasedFromId === "Others" && (
                <div>
                  <label className={labelClass}>Purchased From (Others)</label>
                  <input
                    className={inputClass}
                    value={vo.purchasedFromOtherName}
                    onChange={(e) =>
                      setVo((p) => ({
                        ...p,
                        purchasedFromOtherName: e.target.value,
                      }))
                    }
                  />
                </div>
              )}

              <div>
                <label className={labelClass}>Invoice No</label>
                <input
                  className={inputClass}
                  value={vo.invoiceNo}
                  onChange={(e) =>
                    setVo((p) => ({ ...p, invoiceNo: e.target.value }))
                  }
                />
              </div>

              <div>
                <label className={labelClass}>Purchase Date</label>
                <input
                  type="date"
                  className={inputClass}
                  value={vo.purchaseDate}
                  onChange={(e) =>
                    setVo((p) => ({ ...p, purchaseDate: e.target.value }))
                  }
                />
              </div>

              <div>
                <label className={labelClass}>Purchase Value</label>
                <input
                  type="number"
                  className={inputClass}
                  value={vo.purchaseValue}
                  onChange={(e) =>
                    setVo((p) => ({ ...p, purchaseValue: e.target.value }))
                  }
                />
              </div>

              <div>
                <label className={labelClass}>No.of Days</label>
                <input
                  className={inputClass}
                  value={vo.noOfDays}
                  onChange={(e) =>
                    setVo((p) => ({ ...p, noOfDays: e.target.value }))
                  }
                />
              </div>

              <div>
                <label className={labelClass}>WDV Percentage(%)</label>
                <input
                  className={inputClass}
                  value={vo.wdvPercentage}
                  onChange={(e) =>
                    setVo((p) => ({ ...p, wdvPercentage: e.target.value }))
                  }
                />
              </div>

              <div>
                <label className={labelClass}>Depreciation Value</label>
                <input
                  className={inputClass}
                  value={vo.depreciationValue}
                  onChange={(e) =>
                    setVo((p) => ({ ...p, depreciationValue: e.target.value }))
                  }
                />
              </div>

              <div>
                <label className={labelClass}>WDV as On</label>
                <input
                  className={inputClass}
                  value={vo.wdvAsOn}
                  onChange={(e) => setVo((p) => ({ ...p, wdvAsOn: e.target.value }))}
                />
              </div>

              <div>
                <label className={labelClass}>Hypothicated</label>
                <input
                  className={inputClass}
                  value={vo.hypothecated}
                  onChange={(e) =>
                    setVo((p) => ({ ...p, hypothecated: e.target.value }))
                  }
                />
              </div>

              <div>
                <label className={labelClass}>Next Calibration Date</label>
                <input
                  type="date"
                  className={inputClass}
                  value={vo.nextCalibrationDate}
                  onChange={(e) =>
                    setVo((p) => ({
                      ...p,
                      nextCalibrationDate: e.target.value,
                    }))
                  }
                />
              </div>
            </div>
          </div>
        </div>

        <div className={`${sectionClass} mt-8`}>
          <h3
            className={`text-lg font-semibold mb-4 ${buttonClass} text-white p-3 rounded-lg`}
          >
            Upload Document (Invoice)
          </h3>

          <div className="flex flex-col items-center gap-4">
            <input
              type="file"
              className="hidden"
              id="voDoc"
              accept=".doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.pdf"
              onChange={(e) => handleVoAttachDoc(e.target.files?.[0])}
            />
            <label htmlFor="voDoc" className={btnGray}>
              <FaUpload /> Choose File
            </label>

            <div className="w-full">
              {vo.invoiceDocs.length === 0 ? (
                <div
                  className={`text-sm text-center ${
                    isDark ? "text-gray-400" : "text-gray-600"
                  }`}
                >
                  No documents attached
                </div>
              ) : (
                <div className="space-y-2">
                  {vo.invoiceDocs.map((d, idx) => (
                    <div
                      key={idx}
                      className={`flex items-center justify-between p-3 rounded-lg border ${
                        isDark
                          ? "border-gray-700 bg-gray-900/30"
                          : "border-gray-200 bg-white"
                      }`}
                    >
                      <div className="text-sm">
                        <div className="font-semibold">{d.docName}</div>
                        <div
                          className={`${
                            isDark ? "text-gray-400" : "text-gray-600"
                          }`}
                        >
                          Type: {d.type}
                        </div>
                      </div>
                      <button
                        className="text-red-500 hover:text-red-600"
                        onClick={() =>
                          setVo((p) => ({
                            ...p,
                            invoiceDocs: p.invoiceDocs.filter((_, i) => i !== idx),
                          }))
                        }
                      >
                        <FaTrash />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="flex justify-center gap-4 mt-8">
          <button className={btnPrimary} onClick={saveVehicleOthers}>
            <FaSave /> {editId ? "Update" : "Save"}
          </button>

          <button
            className={btnGray}
            onClick={() => {
              setVo((p) => ({
                ...p,
                description: "",
                idCodePrefix: "",
                idCodeStart: "",
                invoiceNo: "",
                machineSrNo: "",
                purchaseDate: "",
                purchasedFromOtherName: "",
                wdvPercentage: "",
                depreciationValue: "",
                wdvAsOn: "",
                noOfDays: "",
                chasisNo: "",
                engineNo: "",
                idle: "",
                hypothecated: "",
                calibratedDate: "",
                nextCalibrationDate: "",
                noOfEntries: "1",
                invoiceDocs: [],
                vehicleRegNo: "",
                vehicleId: "Select",
              }));
              toast.info("Cleared");
            }}
          >
            <FaSyncAlt /> Add New
          </button>
        </div>
      </>
    );
  };

  const renderShuttering = () => {
    return (
      <>
                <div className={sectionClass}>
          <h3
            className={`text-lg font-semibold mb-4 ${buttonClass} text-white p-3 rounded-lg`}
          >
            Scrap
          </h3>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div>
              <label className={labelClass}>Asset Name *</label>
              <select
                className={inputClass}
                value={sm.assetTypeId}
                onChange={(e) =>
                  setSm((p) => ({
                    ...p,
                    assetTypeId: e.target.value,
                    assetSubTypeId: "Select",
                  }))
                }
              >
                <option value="Select">Select</option>
                {dd.assetTypesS.map((o) => (
                  <option key={o.key} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className={labelClass}>Asset Sub Type Name</label>
              <select
                className={inputClass}
                value={sm.assetSubTypeId}
                onChange={(e) =>
                  setSm((p) => ({ ...p, assetSubTypeId: e.target.value }))
                }
              >
                <option value="Select">Select</option>
                {subtypesS.map((o) => (
                  <option key={o.key} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className={labelClass}>ID Code.No *</label>
              <input
                className={inputClass}
                value={sm.idCodeNo}
                onChange={(e) => setSm((p) => ({ ...p, idCodeNo: e.target.value }))}
              />
            </div>

            <div>
              <label className={labelClass}>Size</label>
              <input
                className={inputClass}
                value={sm.size}
                onChange={(e) => setSm((p) => ({ ...p, size: e.target.value }))}
              />
            </div>

            <div>
              <label className={labelClass}>Make</label>
              <select
                className={inputClass}
                value={sm.makeId}
                onChange={(e) => setSm((p) => ({ ...p, makeId: e.target.value }))}
              >
                <option value="Select">Select</option>
                {(dd.makesS || []).map((x, idx) => (
                  <option key={idx} value={String(x.fld_Makeid || x.id || "")}>
                    {x.Make || x.name || x.text || "Make"}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className={labelClass}>Invoice No</label>
              <input
                className={inputClass}
                value={sm.invoiceNo}
                onChange={(e) => setSm((p) => ({ ...p, invoiceNo: e.target.value }))}
              />
            </div>

            <div>
              <label className={labelClass}>Purchase Date</label>
              <input
                type="date"
                className={inputClass}
                value={sm.purchaseDate}
                onChange={(e) =>
                  setSm((p) => ({ ...p, purchaseDate: e.target.value }))
                }
              />
            </div>

            <div>
              <label className={labelClass}>Location *</label>
             <select
  className={inputClass}
  value={sm.locationText}
  onChange={(e) =>
    setSm((p) => ({ ...p, locationText: e.target.value }))
  }
>
  <option value="Select">Select</option>
  {(dd.locationsS || []).map((x, idx) => (
    <option key={idx} value={x.fld_BranchName || x.name || x.text || ""}>
      {x.fld_BranchName || x.name || x.text || "Branch"}
    </option>
  ))}
</select>
            </div>

            <div>
              <label className={labelClass}>Purchase Value</label>
              <input
                type="number"
                className={inputClass}
                value={sm.purchaseValue}
                onChange={(e) =>
                  setSm((p) => ({ ...p, purchaseValue: e.target.value }))
                }
              />
            </div>

            <div>
              <label className={labelClass}>No of Qty</label>
              <input
                type="number"
                className={inputClass}
                value={sm.noOfQty}
                onChange={(e) => setSm((p) => ({ ...p, noOfQty: e.target.value }))}
              />
            </div>

            <div>
              <label className={labelClass}>Total Value</label>
              <input className={inputClass} value={sm.totalValue} readOnly />
            </div>
          </div>
        </div>

        <div className={`${sectionClass} mt-8`}>
          <h3
            className={`text-lg font-semibold mb-4 ${buttonClass} text-white p-3 rounded-lg`}
          >
            Upload Document (Invoice)
          </h3>

          <div className="flex flex-col items-center gap-4">
            <input
              type="file"
              className="hidden"
              id="smDoc"
              accept=".doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.pdf"
              onChange={(e) => handleSmAttachDoc(e.target.files?.[0])}
            />
            <label htmlFor="smDoc" className={btnGray}>
              <FaUpload /> Choose File
            </label>

            <div className="w-full">
              {sm.invoiceDocs.length === 0 ? (
                <div
                  className={`text-sm text-center ${
                    isDark ? "text-gray-400" : "text-gray-600"
                  }`}
                >
                  No documents attached
                </div>
              ) : (
                <div className="space-y-2">
                  {sm.invoiceDocs.map((d, idx) => (
                    <div
                      key={idx}
                      className={`flex items-center justify-between p-3 rounded-lg border ${
                        isDark
                          ? "border-gray-700 bg-gray-900/30"
                          : "border-gray-200 bg-white"
                      }`}
                    >
                      <div className="text-sm">
                        <div className="font-semibold">{d.docName}</div>
                        <div
                          className={`${
                            isDark ? "text-gray-400" : "text-gray-600"
                          }`}
                        >
                          Type: {d.type}
                        </div>
                      </div>
                      <button
                        className="text-red-500 hover:text-red-600"
                        onClick={() =>
                          setSm((p) => ({
                            ...p,
                            invoiceDocs: p.invoiceDocs.filter((_, i) => i !== idx),
                          }))
                        }
                      >
                        <FaTrash />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="flex justify-center gap-4 mt-8">
          <button className={btnPrimary} onClick={saveShuttering}>
            <FaSave /> {editId ? "Update" : "Save"}
          </button>

          <button
            className={btnGray}
            onClick={() => {
              setSm({
                assetTypeId: "Select",
                assetSubTypeId: "Select",
                makeId: "Select",
                idCodeNo: "",
                size: "",
                invoiceNo: "",
                purchaseDate: "",
                locationText: "Select",
                purchaseValue: "",
                noOfQty: "",
                totalValue: "",
                invoiceDocs: [],
              });
              toast.info("Cleared");
            }}
          >
            <FaSyncAlt /> Add New
          </button>
        </div>
      </>
    );
  };

  const renderLand = () => {
    return (
      <>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div className={sectionClass}>
            <h3
              className={`text-lg font-semibold mb-4 ${buttonClass} text-white p-3 rounded-lg`}
            >
              Asset Details (Land)
            </h3>

            <div className="space-y-4">
              <div>
                <label className={labelClass}>Asset Class *</label>
                <select
                  className={inputClass}
                  value={land.assetClassId}
                  onChange={(e) =>
                    setLand((p) => ({
                      ...p,
                      assetClassId: e.target.value,
                      assetSubTypeId: "Select",
                    }))
                  }
                >
                  <option value="Select">Select</option>
                  {dd.assetClassesL.map((o) => (
                    <option key={o.key} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className={labelClass}>Asset Sub Type Name</label>
                <select
                  className={inputClass}
                  value={land.assetSubTypeId}
                  onChange={(e) =>
                    setLand((p) => ({ ...p, assetSubTypeId: e.target.value }))
                  }
                >
                  <option value="Select">Select</option>
                  {subtypesL.map((o) => (
                    <option key={o.key} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className={labelClass}>Asset Code *</label>
                <input
                  className={inputClass}
                  value={land.assetCode}
                  onChange={(e) =>
                    setLand((p) => ({ ...p, assetCode: e.target.value }))
                  }
                />
              </div>

              <div>
                <label className={labelClass}>Plot Number *</label>
                <input
                  className={inputClass}
                  value={land.plotNumber}
                  onChange={(e) =>
                    setLand((p) => ({ ...p, plotNumber: e.target.value }))
                  }
                />
              </div>

              <div>
                <label className={labelClass}>Location *</label>
                <select
  className={inputClass}
  value={land.location}
  onChange={(e) =>
    setLand((p) => ({
      ...p,
      location: e.target.value,
    }))
  }
>
  <option value="Select">Select</option>
  {(dd.locations || []).map((x, idx) => (
    <option key={idx} value={x.fld_BranchName}>
      {x.fld_BranchName}
    </option>
  ))}
</select>
              </div>

              <div>
                <label className={labelClass}>Address *</label>
                <textarea
                  className={inputClass}
                  rows={3}
                  value={land.address}
                  onChange={(e) =>
                    setLand((p) => ({ ...p, address: e.target.value }))
                  }
                />
              </div>

              <div>
                <label className={labelClass}>Property in Name of *</label>
                <textarea
                  className={inputClass}
                  rows={3}
                  value={land.propertyInNameOf}
                  onChange={(e) =>
                    setLand((p) => ({
                      ...p,
                      propertyInNameOf: e.target.value,
                    }))
                  }
                />
              </div>

              <div>
                <label className={labelClass}>Patta No *</label>
                <input
                  className={inputClass}
                  value={land.pattaNo}
                  onChange={(e) =>
                    setLand((p) => ({ ...p, pattaNo: e.target.value }))
                  }
                />
              </div>

              <div>
                <label className={labelClass}>Survey No *</label>
                <input
                  className={inputClass}
                  value={land.surveyNo}
                  onChange={(e) =>
                    setLand((p) => ({ ...p, surveyNo: e.target.value }))
                  }
                />
              </div>

              <div>
                <label className={labelClass}>Panchayath No</label>
                <input
                  className={inputClass}
                  value={land.panchayathNo}
                  onChange={(e) =>
                    setLand((p) => ({ ...p, panchayathNo: e.target.value }))
                  }
                />
              </div>

              <div>
                <label className={labelClass}>Seller Name *</label>
                <input
                  className={inputClass}
                  value={land.sellerName}
                  onChange={(e) =>
                    setLand((p) => ({ ...p, sellerName: e.target.value }))
                  }
                />
              </div>

              <div>
                <label className={labelClass}>Extend *</label>
                <input
                  className={inputClass}
                  value={land.extend}
                  onChange={(e) =>
                    setLand((p) => ({ ...p, extend: e.target.value }))
                  }
                />
              </div>

              <div>
                <label className={labelClass}>Date of Purchase *</label>
                <input
                  type="date"
                  className={inputClass}
                  value={land.acquisitionDate}
                  onChange={(e) =>
                    setLand((p) => ({
                      ...p,
                      acquisitionDate: e.target.value,
                    }))
                  }
                />
              </div>

              <div>
                <label className={labelClass}>Purchase Value</label>
                <input
                  type="number"
                  className={inputClass}
                  value={land.purchaseValue}
                  onChange={(e) =>
                    setLand((p) => ({ ...p, purchaseValue: e.target.value }))
                  }
                />
              </div>

              <div>
                <label className={labelClass}>Original Document</label>
                <input
                  className={inputClass}
                  value={land.originalDocument}
                  onChange={(e) =>
                    setLand((p) => ({
                      ...p,
                      originalDocument: e.target.value,
                    }))
                  }
                />
              </div>

              <div>
                <label className={labelClass}>Narration</label>
                <textarea
                  className={inputClass}
                  rows={2}
                  value={land.narration}
                  onChange={(e) =>
                    setLand((p) => ({ ...p, narration: e.target.value }))
                  }
                />
              </div>

              <div className="pt-3">
                <div
                  className={`text-sm font-semibold mb-2 ${
                    isDark ? "text-gray-200" : "text-gray-800"
                  }`}
                >
                  Sale Details (Optional)
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                                   <div>
                    <label className={labelClass}>Sale Date</label>
                    <input
                      type="date"
                      className={inputClass}
                      value={land.saleDate}
                      min={land.acquisitionDate || undefined}
                      onChange={(e) =>
                        setLand((p) => ({ ...p, saleDate: e.target.value }))
                      }
                    />
                  </div>
                  <div>
                    <label className={labelClass}>Sale Extent</label>
                    <input
                      className={inputClass}
                      value={land.saleExtent}
                      onChange={(e) =>
                        setLand((p) => ({ ...p, saleExtent: e.target.value }))
                      }
                    />
                  </div>
                  <div>
                    <label className={labelClass}>Sale Extent Rate</label>
                    <input
                      className={inputClass}
                      value={land.saleExtentRate}
                      onChange={(e) =>
                        setLand((p) => ({ ...p, saleExtentRate: e.target.value }))
                      }
                    />
                  </div>
                  <div>
                    <label className={labelClass}>Sale Amount</label>
                    <input
                      className={inputClass}
                      value={land.saleAmount}
                      onChange={(e) =>
                        setLand((p) => ({ ...p, saleAmount: e.target.value }))
                      }
                    />
                  </div>
                  <div>
                    <label className={labelClass}>Sale Receiver</label>
                    <input
                      className={inputClass}
                      value={land.saleReceiver}
                      onChange={(e) =>
                        setLand((p) => ({ ...p, saleReceiver: e.target.value }))
                      }
                    />
                  </div>
                  <div>
                    <label className={labelClass}>Sale Remark</label>
                    <input
                      className={inputClass}
                      value={land.saleRemark}
                      onChange={(e) =>
                        setLand((p) => ({ ...p, saleRemark: e.target.value }))
                      }
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className={sectionClass}>
            <h3
              className={`text-lg font-semibold mb-4 ${buttonClass} text-white p-3 rounded-lg`}
            >
              Tax Payment Details
            </h3>

            <div className="space-y-4">
              <div>
                <label className={labelClass}>Tax Type *</label>
                <select
                  className={inputClass}
                  value={land.taxEntry.taxId}
                  onChange={(e) =>
                    setLand((p) => ({
                      ...p,
                      taxEntry: { ...p.taxEntry, taxId: e.target.value },
                    }))
                  }
                >
                  <option value="Select">Select</option>
                  {(dd.taxTypes || []).map((x, idx) => (
                    <option key={idx} value={String(x.fld_taxid)}>
                      {x.fld_taxtype}
                    </option>
                  ))}
                </select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={labelClass}>Year From *</label>
                  <input
                    className={inputClass}
                    value={land.taxEntry.yearFrom}
                    onChange={(e) =>
                      setLand((p) => ({
                        ...p,
                        taxEntry: { ...p.taxEntry, yearFrom: e.target.value },
                      }))
                    }
                  />
                </div>
                <div>
                  <label className={labelClass}>Year To *</label>
                  <input
                    className={inputClass}
                    value={land.taxEntry.yearTo}
                    onChange={(e) =>
                      setLand((p) => ({
                        ...p,
                        taxEntry: { ...p.taxEntry, yearTo: e.target.value },
                      }))
                    }
                  />
                </div>
              </div>

              <div>
                <label className={labelClass}>Payment details *</label>
                <textarea
                  className={inputClass}
                  rows={2}
                  value={land.taxEntry.paydet}
                  onChange={(e) =>
                    setLand((p) => ({
                      ...p,
                      taxEntry: { ...p.taxEntry, paydet: e.target.value },
                    }))
                  }
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={labelClass}>Paid Date *</label>
                  <input
                    type="date"
                    className={inputClass}
                    value={land.taxEntry.paidDate}
                    onChange={(e) =>
                      setLand((p) => ({
                        ...p,
                        taxEntry: { ...p.taxEntry, paidDate: e.target.value },
                      }))
                    }
                  />
                </div>
                <div>
                  <label className={labelClass}>Next Renewal Date *</label>
                  <input
                    type="date"
                    className={inputClass}
                    value={land.taxEntry.renewalDate}
                    onChange={(e) =>
                      setLand((p) => ({
                        ...p,
                        taxEntry: {
                          ...p.taxEntry,
                          renewalDate: e.target.value,
                        },
                      }))
                    }
                  />
                </div>
              </div>

              <div>
                <label className={labelClass}>Amount *</label>
                <input
                  type="number"
                  className={inputClass}
                  value={land.taxEntry.amount}
                  onChange={(e) =>
                    setLand((p) => ({
                      ...p,
                      taxEntry: { ...p.taxEntry, amount: e.target.value },
                    }))
                  }
                />
              </div>

              <div>
                <label className={labelClass}>Narration</label>
                <textarea
                  className={inputClass}
                  rows={2}
                  value={land.taxEntry.narration}
                  onChange={(e) =>
                    setLand((p) => ({
                      ...p,
                      taxEntry: { ...p.taxEntry, narration: e.target.value },
                    }))
                  }
                />
              </div>

              <div className="flex justify-center pt-2">
                <button className={btnPrimary} onClick={addTaxRow}>
                  <FaPlus /> Add
                </button>
              </div>

              <div className="pt-2">
                <div
                  className={`text-sm font-semibold mb-2 ${
                    isDark ? "text-gray-200" : "text-gray-800"
                  }`}
                >
                  Added Tax Rows ({land.taxDetails.length})
                </div>

                {land.taxDetails.length === 0 ? (
                  <div
                    className={`text-sm ${
                      isDark ? "text-gray-400" : "text-gray-600"
                    }`}
                  >
                    No tax rows
                  </div>
                ) : (
                  <div className="space-y-2">
                    {land.taxDetails.map((t, idx) => (
                      <div
                        key={idx}
                        className={`p-3 rounded-lg border flex items-center justify-between ${
                          isDark
                            ? "border-gray-700 bg-gray-900/30"
                            : "border-gray-200 bg-white"
                        }`}
                      >
                        <div className="text-sm">
                          <div className="font-semibold">{t.taxName}</div>
                          <div
                            className={`${
                              isDark ? "text-gray-400" : "text-gray-600"
                            }`}
                          >
                            {t.yearFrom}-{t.yearTo} | Paid: {t.paidDate} | Renewal:{" "}
                            {t.renewalDate} | Amt: {t.amount}
                          </div>
                        </div>
                        <button
                          className="text-red-500 hover:text-red-600"
                          onClick={() => removeTaxRow(idx)}
                        >
                          <FaTrash />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className={`${sectionClass} mt-8`}>
          <h3
            className={`text-lg font-semibold mb-4 ${buttonClass} text-white p-3 rounded-lg`}
          >
            Upload Document
          </h3>

          <div className="flex flex-wrap items-center gap-6 mb-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="landDocType"
                checked={land.docTypeRadio === "land"}
                onChange={() => setLand((p) => ({ ...p, docTypeRadio: "land" }))}
              />
              <span className={isDark ? "text-gray-300" : "text-gray-700"}>
                Land Document
              </span>
            </label>

            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="landDocType"
                checked={land.docTypeRadio === "tax"}
                onChange={() => setLand((p) => ({ ...p, docTypeRadio: "tax" }))}
              />
              <span className={isDark ? "text-gray-300" : "text-gray-700"}>
                Tax Document
              </span>
            </label>
          </div>

          <div className="flex flex-col items-center gap-4">
            <input
              type="file"
              className="hidden"
              id="landDoc"
              accept=".doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.pdf"
              onChange={(e) => handleLandAttachDoc(e.target.files?.[0])}
            />
            <label htmlFor="landDoc" className={btnGray}>
              <FaUpload /> Choose File
            </label>

            <div className="w-full">
              {land.documents.length === 0 ? (
                <div
                  className={`text-sm text-center ${
                    isDark ? "text-gray-400" : "text-gray-600"
                  }`}
                >
                  No documents attached
                </div>
              ) : (
                <div className="space-y-2">
                  {land.documents.map((d, idx) => (
                    <div
                      key={idx}
                      className={`flex items-center justify-between p-3 rounded-lg border ${
                        isDark
                          ? "border-gray-700 bg-gray-900/30"
                          : "border-gray-200 bg-white"
                      }`}
                    >
                      <div className="text-sm">
                        <div className="font-semibold">{d.docName}</div>
                        <div
                          className={`${
                            isDark ? "text-gray-400" : "text-gray-600"
                          }`}
                        >
                          Type: {d.docType}
                        </div>
                      </div>
                      <button
                        className="text-red-500 hover:text-red-600"
                        onClick={() =>
                          setLand((p) => ({
                            ...p,
                            documents: p.documents.filter((_, i) => i !== idx),
                          }))
                        }
                      >
                        <FaTrash />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="flex justify-center gap-4 mt-8">
          <button className={btnPrimary} onClick={saveLand}>
            <FaSave /> {editId ? "Update" : "Save"}
          </button>

          <button
            className={btnGray}
            onClick={() => {
              setLand((p) => ({
                ...p,
                assetClassId: "Select",
                assetSubTypeId: "Select",
                assetCode: "",
                plotNumber: "",
                location: "Select",
                address: "",
                propertyInNameOf: "",
                pattaNo: "",
                surveyNo: "",
                panchayathNo: "",
                sellerName: "",
                extend: "",
                acquisitionDate: "",
                purchaseValue: "",
                originalDocument: "",
                narration: "",
                saleDate: "",
                saleExtent: "",
                saleExtentRate: "",
                saleAmount: "",
                saleReceiver: "",
                saleRemark: "",
                taxDetails: [],
                documents: [],
                taxEntry: {
                  taxId: "Select",
                  yearFrom: "",
                  yearTo: "",
                  paydet: "",
                  paidDate: "",
                  renewalDate: "",
                  amount: "",
                  narration: "",
                },
                docTypeRadio: "land",
              }));
              toast.info("Cleared");
            }}
          >
            <FaSyncAlt /> Add New
          </button>
        </div>
      </>
    );
  };

  return (
    <AssetLayoutWrapper>
      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">ASSET MASTER</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />
        <button className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-xl border transition ${
            isDark
              ? "border-slate-600 text-slate-300 hover:bg-slate-800"
              : "border-slate-300 text-slate-700 hover:bg-slate-100"
          }`} onClick={() => navigate("/Asset/AssetMasterListing")}>
                <FaArrowLeft /> Back
              </button>
        <div className={`${cardClass} p-6 mt-6`}>
          <div className="flex flex-wrap justify-between items-center gap-4 mb-6">
            <h1 className={`text-2xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
              Asset Master
            </h1>

            <div className="flex gap-3">
            </div>
          </div>

          {topRadio}

          {panelType === "L" && renderLand()}
          {(panelType === "V" || panelType === "O") && renderVehicleOthers()}
          {panelType === "S" && renderShuttering()}

          <ToastContainer
            position="top-right"
            autoClose={3000}
            hideProgressBar={false}
            closeOnClick
            pauseOnHover
            theme={isDark ? "dark" : "light"}
          />
        </div>
      </div>
    </AssetLayoutWrapper>
  );
};

export default AssetMaster;